import { NotificationBell } from '../components/notifications/NotificationBell';
import { FeedbackButton } from '../components/feedback/FeedbackButton';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useState, Suspense } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useAuth } from '../hooks/useAuth';
import { LoadingState } from '../components/ui/loading-state';
import { usePermissions } from '../hooks/usePermissions';
import { useTheme } from '../hooks/useTheme';
import { SyncStatusIndicator } from '../components/SyncStatusIndicator';
import { ConfirmProvider } from '../components/ui/confirm-dialog';
import {
  LayoutDashboard,
  Building2,
  MapPin,
  ClipboardCheck,
  Calendar,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Sun,
  Moon,
  User,
  Target,
  Briefcase,
  Users,
  Inbox,
  GraduationCap,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { authService } from '../services/authService';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Clients', href: '/dashboard/clients', icon: Building2 },
  { name: 'Sites', href: '/dashboard/sites', icon: MapPin },
  { name: 'Inspections', href: '/dashboard/inspections', icon: ClipboardCheck },
  { name: 'Schedule', href: '/dashboard/schedule', icon: Calendar },
  { name: 'Reports', href: '/dashboard/reports', icon: BarChart3 },
  {
    name: 'People Hub',
    href: '/dashboard/people',
    icon: Users,
    adminOnly: true,
  },
  { name: 'Learn', href: '/dashboard/learn', icon: GraduationCap },
  { name: 'Admin', href: '/dashboard/admin', icon: Settings, adminOnly: true },
] as const;

// Admin navigation removed - now handled by unified AdminPortal at /dashboard/admin

// Consultant navigation (shown for consultant and admin roles)
const consultantNavigation = [
  {
    name: 'Lead Pipeline',
    href: '/dashboard/consultant',
    icon: Target,
  },
  {
    name: 'Client Management',
    href: '/dashboard/client-management',
    icon: Briefcase,
  },
];

// Data Recon inbox (Jim's-USB migration, 2026-09-29): every staff role
// except inspector triages unassigned files, gated on recon:read.
const reconNavigation = [
  { name: 'Data Recon', href: '/dashboard/recon', icon: Inbox },
];

export function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  // Sign out through useAuth so the previous account's query/API caches are
  // purged — a bare store logout leaves them for the next login (Jim, 8/4).
  const { signOut } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  // Pending users count for People Hub badge
  const { isAdmin, can } = usePermissions();
  const { data: pendingUsers } = useQuery({
    queryKey: ['pending-users'],
    queryFn: authService.getPendingUsers,
    refetchInterval: 30000,
    enabled: isAdmin,
  });
  const pendingCount = pendingUsers?.length ?? 0;

  const filteredNavigation = navigation.filter(
    (item) => !('adminOnly' in item && item.adminOnly) || isAdmin
  );

  return (
    <ConfirmProvider>
      <div className="flex h-screen bg-slate-50 dark:bg-slate-900">
        {/* Sidebar - Desktop */}
        <aside className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
          <div className="flex flex-col flex-grow bg-slate-900 dark:bg-slate-950 overflow-y-auto">
            {/* Logo */}
            <div className="flex items-center h-16 px-6 border-b border-slate-800">
              <Link
                to="/select-mode"
                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              >
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <span className="text-lg font-semibold text-white">
                  EnviroHub
                </span>
              </Link>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-3 py-4 space-y-1">
              {filteredNavigation.map((item) => {
                const isActive =
                  location.pathname === item.href ||
                  (item.href !== '/dashboard' &&
                    location.pathname.startsWith(item.href));
                const Icon = item.icon;
                const badge =
                  item.name === 'People Hub' && pendingCount > 0
                    ? pendingCount
                    : 0;
                const navClassName = `
                    flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                    transition-all duration-200 relative w-full text-left
                    ${
                      isActive
                        ? 'bg-primary-600/20 text-primary-400 border-l-2 border-primary-500 -ml-[2px] pl-[14px]'
                        : 'text-slate-400 hover:text-white hover:bg-slate-800'
                    }
                  `;
                if (item.name === 'People Hub') {
                  return (
                    <button
                      key={item.name}
                      onClick={() =>
                        navigate('/dashboard/people', {
                          state: { fromNav: Date.now() },
                        })
                      }
                      className={navClassName}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      {item.name}
                      {badge > 0 && (
                        <span className="ml-auto px-1.5 py-0.5 text-xs font-bold text-white bg-amber-500 rounded-full">
                          {badge > 9 ? '9+' : badge}
                        </span>
                      )}
                    </button>
                  );
                }
                return (
                  <Link key={item.name} to={item.href} className={navClassName}>
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {item.name}
                    {badge > 0 && (
                      <span className="ml-auto px-1.5 py-0.5 text-xs font-bold text-white bg-amber-500 rounded-full">
                        {badge > 9 ? '9+' : badge}
                      </span>
                    )}
                  </Link>
                );
              })}

              {/* Business Dev (consultant/admin only). Was gated on
                  compliance:read, which manager/inspector/client/viewer all
                  hold — four roles saw Lead Pipeline links that always 403'd
                  (role audit 2026-08-20). leads:read is the real contract. */}
              {(can('leads:read') || can('admin:access')) && (
                <>
                  <div className="pt-4 pb-2">
                    <div className="flex items-center gap-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <Target className="w-3.5 h-3.5" />
                      Business Dev
                    </div>
                  </div>
                  {consultantNavigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`
                        flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                        transition-all duration-200
                        ${
                          isActive
                            ? 'bg-primary-600/20 text-primary-400 border-l-2 border-primary-500 -ml-[2px] pl-[14px]'
                            : 'text-slate-400 hover:text-white hover:bg-slate-800'
                        }
                      `}
                      >
                        <Icon className="w-5 h-5 flex-shrink-0" />
                        {item.name}
                      </Link>
                    );
                  })}
                </>
              )}

              {can('recon:read') && (
                <>
                  <div className="pt-4 pb-2">
                    <div className="flex items-center gap-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <Inbox className="w-3.5 h-3.5" />
                      Data Recon
                    </div>
                  </div>
                  {reconNavigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`
                        flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                        transition-all duration-200
                        ${
                          isActive
                            ? 'bg-primary-600/20 text-primary-400 border-l-2 border-primary-500 -ml-[2px] pl-[14px]'
                            : 'text-slate-400 hover:text-white hover:bg-slate-800'
                        }
                      `}
                      >
                        <Icon className="w-5 h-5 flex-shrink-0" />
                        {item.name}
                      </Link>
                    );
                  })}
                </>
              )}

              {/* Admin Section */}
              {/* Old admin navigation removed - now handled by unified AdminPortal */}
            </nav>

            {/* Sync Status */}
            <div className="px-4 py-3 border-t border-slate-800">
              <SyncStatusIndicator />
            </div>

            {/* User section */}
            <div className="p-4 border-t border-slate-800">
              <div className="flex items-center gap-3">
                <Link
                  to="/dashboard/settings"
                  className="flex items-center gap-3 flex-1 min-w-0 rounded-lg p-1 -m-1 hover:bg-slate-800 transition-colors cursor-pointer"
                  title="Account Settings"
                >
                  <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center">
                    <User className="w-5 h-5 text-slate-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-slate-500 truncate capitalize">
                      {user?.role}
                    </p>
                  </div>
                </Link>
                <button
                  onClick={signOut}
                  className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                  title="Logout"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </aside>

        {/* Main content area */}
        {/* min-w-0: without it this flex item sizes to its widest child
            (min-width:auto), so one wide table forces the whole page to
            scroll horizontally instead of the table's own overflow-x-auto */}
        <div className="lg:pl-64 flex flex-col flex-1 min-w-0">
          {/* Top header bar */}
          <header className="sticky top-0 z-40 h-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between h-full px-4 lg:px-6">
              {/* Mobile nav lives in the bottom bar (Menu opens the
                  bottom sheet) — no top hamburger. Spacer keeps the
                  action icons right-aligned at every width. */}
              <div className="flex flex-1" />

              {/* Right side actions */}
              <div className="flex items-center gap-2">
                {/* Dark mode toggle */}
                <button
                  onClick={toggleDarkMode}
                  className="p-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                  title={
                    isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'
                  }
                >
                  {isDarkMode ? (
                    <Sun className="w-5 h-5" />
                  ) : (
                    <Moon className="w-5 h-5" />
                  )}
                </button>

                {/* Notifications — real bell (was a dead button with a
                    hardcoded red dot; Jim 2026-08-12) */}
                <FeedbackButton context="hub" />
                <NotificationBell context="hub" />

                {/* User menu - desktop */}
                <div className="hidden lg:block relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center gap-2 p-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                      <span className="text-sm font-medium text-primary-700 dark:text-primary-400">
                        {user?.firstName?.[0]}
                        {user?.lastName?.[0]}
                      </span>
                    </div>
                    <ChevronDown className="w-4 h-4" />
                  </button>

                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-lg shadow-soft-lg border border-slate-200 dark:border-slate-700 py-1 animate-fade-in">
                      <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-700">
                        <p className="text-sm font-medium text-slate-900 dark:text-white">
                          {user?.firstName} {user?.lastName}
                        </p>
                        <p className="text-xs text-slate-500">{user?.email}</p>
                      </div>
                      <Link
                        to="/dashboard/settings"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <Settings className="w-4 h-4" />
                        Settings
                      </Link>
                      <button
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          signOut();
                        }}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-danger-600 dark:text-danger-400 hover:bg-slate-50 dark:hover:bg-slate-700"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </header>

          {/* Page content — pages are code-split; the sidebar/header stay
              mounted while a page chunk loads. pb-20 clears the mobile
              bottom nav; lg has no bottom nav so padding resets */}
          <main className="flex-1 overflow-y-auto">
            <div className="p-4 lg:p-6 pb-20 lg:pb-6">
              <Suspense fallback={<LoadingState size="lg" />}>
                <Outlet />
              </Suspense>
            </div>
          </main>

          {/* Mobile bottom nav — thumb-reach for the four highest-traffic
              sections (lite's LiteLayout pattern); everything else stays in
              the hamburger drawer via Menu */}
          <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 safe-area-inset-bottom">
            <div className="flex justify-around">
              {filteredNavigation
                .filter((item) =>
                  // Inspections lives in the Menu sheet; Clients earns the
                  // thumb slot (Chris, 2026-07-06)
                  ['Dashboard', 'Clients', 'Sites', 'Schedule'].includes(
                    item.name
                  )
                )
                .map((item) => {
                  const isActive =
                    location.pathname === item.href ||
                    (item.href !== '/dashboard' &&
                      location.pathname.startsWith(item.href));
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`flex flex-col items-center gap-0.5 py-2 min-w-[60px] text-[11px] font-medium transition-colors ${
                        isActive
                          ? 'text-primary-600 dark:text-primary-400'
                          : 'text-slate-500 dark:text-slate-400'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      {item.name}
                    </Link>
                  );
                })}
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="flex flex-col items-center gap-0.5 py-2 min-w-[60px] text-[11px] font-medium text-slate-500 dark:text-slate-400"
              >
                <Menu className="w-5 h-5" />
                Menu
              </button>
            </div>
          </nav>
        </div>

        {/* Mobile menu overlay */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-[1000]">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Bottom sheet — rises from the bottom nav for one-handed
                reach (was a left drawer). Mobile-only; desktop keeps the
                fixed sidebar. */}
            <div className="fixed bottom-0 left-0 right-0 max-h-[75vh] flex flex-col rounded-t-2xl bg-slate-900 dark:bg-slate-950 animate-sheet-up">
              {/* Drag-handle affordance */}
              <div className="flex justify-center pt-2.5 pb-1">
                <div className="w-10 h-1 rounded-full bg-slate-700" />
              </div>
              {/* Header */}
              <div className="flex items-center justify-between h-12 px-4 border-b border-slate-800">
                <Link
                  to="/select-mode"
                  className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-lg font-semibold text-white">
                    EnviroHub
                  </span>
                </Link>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Navigation — scrolls within the sheet if it outgrows 75vh */}
              <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
                {filteredNavigation.map((item) => {
                  const isActive =
                    location.pathname === item.href ||
                    (item.href !== '/dashboard' &&
                      location.pathname.startsWith(item.href));
                  const Icon = item.icon;
                  const badge =
                    item.name === 'People Hub' && pendingCount > 0
                      ? pendingCount
                      : 0;
                  const mobileNavClass = `
                      flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                      transition-all duration-200 w-full text-left
                      ${
                        isActive
                          ? 'bg-primary-600/20 text-primary-400'
                          : 'text-slate-400 hover:text-white hover:bg-slate-800'
                      }
                    `;
                  if (item.name === 'People Hub') {
                    return (
                      <button
                        key={item.name}
                        onClick={() => {
                          setIsMobileMenuOpen(false);
                          navigate('/dashboard/people', {
                            state: { fromNav: Date.now() },
                          });
                        }}
                        className={mobileNavClass}
                      >
                        <Icon className="w-5 h-5" />
                        {item.name}
                        {badge > 0 && (
                          <span className="ml-auto px-1.5 py-0.5 text-xs font-bold text-white bg-amber-500 rounded-full">
                            {badge > 9 ? '9+' : badge}
                          </span>
                        )}
                      </button>
                    );
                  }
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={mobileNavClass}
                    >
                      <Icon className="w-5 h-5" />
                      {item.name}
                      {badge > 0 && (
                        <span className="ml-auto px-1.5 py-0.5 text-xs font-bold text-white bg-amber-500 rounded-full">
                          {badge > 9 ? '9+' : badge}
                        </span>
                      )}
                    </Link>
                  );
                })}

                {/* Old mobile admin navigation removed - now handled by unified AdminPortal */}
              </nav>

              {/* User section — static footer of the sheet; safe-area pad
                  keeps sign-out clear of the iPhone home indicator */}
              <div className="p-4 border-t border-slate-800 safe-area-inset-bottom">
                <div className="flex items-center gap-3">
                  <Link
                    to="/dashboard/settings"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 flex-1 min-w-0 rounded-lg p-1 -m-1 hover:bg-slate-800 transition-colors cursor-pointer"
                    title="Account Settings"
                  >
                    <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                      <User className="w-5 h-5 text-slate-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-xs text-slate-500 truncate capitalize">
                        {user?.role}
                      </p>
                    </div>
                  </Link>
                  <button
                    onClick={signOut}
                    className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ConfirmProvider>
  );
}
